import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getInboundMasterUnit,
  markInboundMasterUnitAsReceived,
  markConsignmentAsShortReceived,
  markAllConsignmentsAsShortReceived,
  removeShortReceived,
  InboundMasterUnit,
  ShortReceivedPayload,
  BulkShortReceivedPayload
} from '../../services/inboundMasterUnitApi';
import { useTheme } from '../../contexts/ThemeContext';
import GenericButton from '../../components/GenericButton';
import GenericModal from '../../components/GenericModal';
import StatusBadge from '../../components/StatusBadge';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  Grid,
  Stack,
  Divider,
  TextField,
  Chip
} from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';

const InboundMasterUnitView: React.FC = () => {
  const { theme } = useTheme();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [masterUnit, setMasterUnit] = useState<InboundMasterUnit | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [showShortReceivedModal, setShowShortReceivedModal] = useState(false);
  const [showBulkShortReceivedModal, setShowBulkShortReceivedModal] = useState(false);
  const [shortReceivedData, setShortReceivedData] = useState<ShortReceivedPayload>({
    consignment_id: 0,
    masterunit_id: 0,
    reason: '',
    reason_attachment: undefined
  });
  const [bulkShortReceivedData, setBulkShortReceivedData] = useState<BulkShortReceivedPayload>({
    masterunit_id: 0,
    consignmentIds: [],
    reason: ''
  });

  useEffect(() => {
    if (id) {
      fetchMasterUnit();
    }
  }, [id]);

  const fetchMasterUnit = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getInboundMasterUnit(Number(id));
      setMasterUnit(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsReceived = async () => {
    if (!masterUnit) return;
    try {
      setActionLoading(true);
      await markInboundMasterUnitAsReceived(masterUnit.id);
      await fetchMasterUnit();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleShortReceived = (consignmentId: number) => {
    if (!masterUnit) return;
    setShortReceivedData({ consignment_id: consignmentId, masterunit_id: masterUnit.id, reason: '' });
    setShowShortReceivedModal(true);
  };

  const handleBulkShortReceived = () => {
    if (!masterUnit) return;
    setBulkShortReceivedData({ masterunit_id: masterUnit.id, consignmentIds: masterUnit.consignments?.map(c => c.id) || [], reason: '' });
    setShowBulkShortReceivedModal(true);
  };

  const submitShortReceived = async () => {
    try {
      setActionLoading(true);
      await markConsignmentAsShortReceived(shortReceivedData);
      setShowShortReceivedModal(false);
      await fetchMasterUnit();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const submitBulkShortReceived = async () => {
    try {
      setActionLoading(true);
      await markAllConsignmentsAsShortReceived(bulkShortReceivedData);
      setShowBulkShortReceivedModal(false);
      await fetchMasterUnit();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveShortReceived = async (consignmentId: number) => {
    if (!masterUnit) return;
    try {
      setActionLoading(true);
      await removeShortReceived(masterUnit.id, consignmentId);
      await fetchMasterUnit();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 256 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !masterUnit) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" color="error" sx={{ mb: 2 }}>Error</Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>{error}</Typography>
          <GenericButton onClick={() => navigate('/inbound-master-units')}>Back to Inbound Master Units</GenericButton>
        </Box>
      </Box>
    );
  }

  if (!masterUnit) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" sx={{ mb: 2 }}>Master Unit Not Found</Typography>
          <GenericButton onClick={() => navigate('/inbound-master-units')}>Back to Inbound Master Units</GenericButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Inbound Master Unit</Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mt: 0.5 }}>MU Number: {masterUnit.mu_number}</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <GenericButton variant="secondary" onClick={() => window.print()}>
            <PrintIcon sx={{ mr: 1 }} /> Print
          </GenericButton>
          <GenericButton variant="outline" onClick={() => navigate('/inbound-master-units')}>Back to List</GenericButton>
          {masterUnit.status_id === 6 && (
            <GenericButton onClick={handleMarkAsReceived} disabled={actionLoading}>
              {actionLoading ? 'Processing...' : 'Mark as Received'}
            </GenericButton>
          )}
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}

      {/* Info */}
      <Card sx={{ mb: 3, bgcolor: theme.palette.background.paper }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Master Unit Information</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="textSecondary" display="block">Seal Number</Typography>
              <Typography variant="body1">{masterUnit.seal_number}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="textSecondary" display="block">Origin</Typography>
              <Typography variant="body1">{masterUnit.originCity?.name || 'N/A'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="textSecondary" display="block">Branch</Typography>
              <Typography variant="body1">{masterUnit.branch?.name || 'N/A'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="textSecondary" display="block">MU Type</Typography>
              <Typography variant="body1">{masterUnit.mu_type}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="textSecondary" display="block">Type</Typography>
              <Typography variant="body1">{masterUnit.type}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="textSecondary" display="block">Journey Type</Typography>
              <Typography variant="body1">{masterUnit.journey_type}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="textSecondary" display="block">User</Typography>
              <Typography variant="body1">{masterUnit.user?.name || 'N/A'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="textSecondary" display="block">Created</Typography>
              <Typography variant="body1">{new Date(masterUnit.created_at).toLocaleDateString()}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="caption" color="textSecondary" display="block">Status</Typography>
              <StatusBadge status={{ id: masterUnit.status_id, name: masterUnit.status?.name || 'Unknown' }} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Consignments */}
      <Card sx={{ bgcolor: theme.palette.background.paper }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">Consignments ({masterUnit.consignments?.length || 0})</Typography>
            <Stack direction="row" spacing={1}>
              <IconButton size="small" onClick={() => { /* print */ }}>
                <PrintIcon />
              </IconButton>
              {masterUnit.status_id === 7 && (
                <GenericButton variant="secondary" onClick={handleBulkShortReceived}>Mark All as Short Received</GenericButton>
              )}
            </Stack>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <TableContainer component={Paper} sx={{ bgcolor: 'transparent' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Consignment #</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Booking Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Service Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Booking Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Weight (Grams)</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status in MU</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {masterUnit.consignments && masterUnit.consignments.length > 0 ? (
                  masterUnit.consignments.map((consignment) => (
                    <TableRow key={consignment.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{consignment.consignment_no}</Typography>
                      </TableCell>
                      <TableCell>{consignment.booking_type}</TableCell>
                      <TableCell>{consignment.service_type}</TableCell>
                      <TableCell>{new Date(consignment.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>{consignment.chargeable_weight}</TableCell>
                      <TableCell>
                        <StatusBadge status={{ id: 0, name: consignment.statusInMasterUnit || 'Unknown' }} />
                      </TableCell>
                      <TableCell>
                        {consignment.statusInMasterUnit === 'Short Received' ? (
                          <GenericButton variant="outline" onClick={() => handleRemoveShortReceived(consignment.id)}>Remove Short</GenericButton>
                        ) : (
                          <GenericButton variant="outline" onClick={() => handleShortReceived(consignment.id)}>Mark Short</GenericButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="textSecondary">No consignments found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Short Received Modal */}
      <GenericModal open={showShortReceivedModal} onClose={() => setShowShortReceivedModal(false)} title="Mark as Short Received">
        <Box component="form" onSubmit={(e: React.FormEvent) => { e.preventDefault(); submitShortReceived(); }} sx={{ display: 'grid', gap: 2 }}>
          <TextField
            label="Reason"
            value={shortReceivedData.reason}
            onChange={(e) => setShortReceivedData((prev: ShortReceivedPayload) => ({ ...prev, reason: e.target.value }))}
            fullWidth
            required
            multiline
            minRows={3}
          />
          <input
            type="file"
            onChange={(e) => setShortReceivedData((prev: ShortReceivedPayload) => ({ ...prev, reason_attachment: e.target.files?.[0] }))}
            accept="image/*,.pdf"
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <GenericButton variant="outline" type="button" onClick={() => setShowShortReceivedModal(false)}>Cancel</GenericButton>
            <GenericButton type="submit" disabled={actionLoading}>{actionLoading ? 'Processing...' : 'Submit'}</GenericButton>
          </Stack>
        </Box>
      </GenericModal>

      {/* Bulk Short Received Modal */}
      <GenericModal open={showBulkShortReceivedModal} onClose={() => setShowBulkShortReceivedModal(false)} title="Mark All as Short Received">
        <Box component="form" onSubmit={(e: React.FormEvent) => { e.preventDefault(); submitBulkShortReceived(); }} sx={{ display: 'grid', gap: 2 }}>
          <TextField
            label="Reason"
            value={bulkShortReceivedData.reason}
            onChange={(e) => setBulkShortReceivedData((prev: BulkShortReceivedPayload) => ({ ...prev, reason: e.target.value }))}
            fullWidth
            required
            multiline
            minRows={3}
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <GenericButton variant="outline" type="button" onClick={() => setShowBulkShortReceivedModal(false)}>Cancel</GenericButton>
            <GenericButton type="submit" disabled={actionLoading}>{actionLoading ? 'Processing...' : 'Submit'}</GenericButton>
          </Stack>
        </Box>
      </GenericModal>
    </Box>
  );
};

export default InboundMasterUnitView;
